import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoginService } from '../login.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import {
  AccessTokenRequest,
  PasswordGrantTokenRequest,
} from 'edu-auth/dist/request/access-token.request';
import { FcmRegistrationService } from '../../notifications/fcm-registration.service';
import { OidcAuthService } from '../../auth/oidc-auth.service';
@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit {
  isLoading: boolean = false;

  loginStream: BehaviorSubject<AccessTokenRequest> = new BehaviorSubject(null);
  environment = environment;
  logoUrl: string;
  // Display name of the identity provider on the "Log in with …" button. Configurable per
  // environment (environment.oidc.providerName), defaults to the tenant org name.
  oidcProviderName = environment.oidc?.providerName ?? environment.org;

  constructor(
    private _loginService: LoginService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    private fcmRegistration: FcmRegistrationService,
    private oidcAuth: OidcAuthService,
  ) {
    this.logoUrl = environment.assets.logo;
  }

  /** True while the silent (prompt=none) redirect is in flight, so the page shows a
   *  "signing you in" placeholder instead of flashing the login button. */
  attemptingSilent = false;

  /** Interactive QLM login (explicit button click) — always shows the AS login page. */
  loginWithQlm(): void {
    this.oidcAuth.startLogin(this.redirectUrl || '/portal/pseudocode');
  }

  /**
   * On load with OIDC enabled: if already signed in, go straight in; otherwise attempt a
   * silent (prompt=none) login once. The callback redirects back to `/login?silent=failed`
   * when there's no AS session, which stops us from re-attempting (no redirect loop) and
   * falls back to showing the "Log in with QLM" button.
   */
  private handleOidcLogin(): void {
    if (this._loginService.isLoggedIn && this._loginService.hasInfo) {
      this.redirect();
      return;
    }
    const silentFailed =
      this.activatedRoute.snapshot.queryParamMap.get('silent') === 'failed';
    if (!silentFailed) {
      this.attemptingSilent = true;
      this.oidcAuth.startLogin(this.redirectUrl || '/portal/pseudocode', {
        prompt: 'none',
      });
      return;
    }
    // Silent attempt already failed — leave the button showing (attemptingSilent = false).
  }

  redirectUrl: string;
  ngOnInit(): void {
    // Check the redirection url
    if (this.activatedRoute.snapshot.queryParamMap.has('redirect_to')) {
      this.redirectUrl =
        this.activatedRoute.snapshot.queryParamMap.get('redirect_to');
    }
    // OIDC-only: try silent SSO on load, fall back to the "Log in with QLM" button.
    this.handleOidcLogin();
  }
  public async login(request: AccessTokenRequest) {
    try {
      this.isLoading = true;
      await this._loginService.login(request as PasswordGrantTokenRequest);
      await this._loginService.getUserInfo();
      await this.fcmRegistration.registerBrowserToken();
      this.redirect();
    } catch (e) {
      this.snackBar.open(e.message, 'dismiss', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
      this.isLoading = false;
    }
  }

  async handleMagicLinkCodeSessionEstablished(): Promise<void> {
    try {
      this.isLoading = true;
      await this.completeSessionAfterMagicLinkTokens();
    } catch (e) {
      this.snackBar.open(e.message ?? 'Unable to complete login', 'dismiss', {
        duration: 3500,
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
      this.isLoading = false;
    }
  }

  private async completeSessionAfterMagicLinkTokens(): Promise<void> {
    await this._loginService.getUserInfo();
    try {
      await this.fcmRegistration.registerBrowserToken();
    } catch {
      // Fail silently; do not block redirect
    }
    this.redirect();
  }

  private redirect() {
    const explicitTarget = this.resolveExplicitRedirectTarget();
    if (explicitTarget != null) {
      this._router.navigateByUrl(explicitTarget);
    } else {
      this._router.navigate(['/portal/pseudocode']);
    }
    this.isLoading = false;
  }

  /**
   * Same rules as plain URL login without redirect_to:
   * honor concrete redirect_to paths from the magic-link or query string.
   * Bare `/portal` is treated as no explicit target so magic-link matches password flow.
   *
   * Default route: translator only exposes `/portal/pseudocode` under portal (see app-routing).
   */
  private resolveExplicitRedirectTarget(): string | null {
    if (this.redirectUrl == null || this.redirectUrl === '') {
      return null;
    }
    const path = decodeURIComponent(this.redirectUrl).trim();
    if (/^\/portal\/?$/i.test(path)) {
      return null;
    }
    return this.redirectUrl;
  }
}
